import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGlobalLeaderboard } from '../../services/firestoreService';
import { auth } from '../../firebase';
import Avatar from '../../Assets/avatar.png';
import { Trophy, Crown, Medal, Clock, ArrowLeft, X } from 'lucide-react';

/**
 * Full-Screen Immersive Leaderboard
 *
 * No sidebar navigation - pure competitive experience
 * Opens in a focused view for maximum engagement
 */
function LeaderboardFullscreen() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('weekly');
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentUser(user);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    loadLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const sunday = new Date();
      sunday.setDate(now.getDate() + ((7 - now.getDay()) % 7));
      sunday.setHours(0, 0, 0, 0);

      const diff = sunday - now;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeRemaining(`${days}d ${hours}h ${minutes}m`);
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, []);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const result = await getGlobalLeaderboard(100);
      if (result.success) {
        let data = result.data;

        if (activeTab === 'weekly') {
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          data = data.filter(entry => {
            const entryDate = entry.completedAt?.toDate?.() || new Date(entry.completedAt);
            return entryDate >= weekAgo;
          });
        }

        data.sort((a, b) => (b.points || b.score || 0) - (a.points || a.score || 0));
        setLeaderboardData(data);
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalColor = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-br from-yellow-400 to-yellow-600';
      case 2:
        return 'bg-gradient-to-br from-slate-300 to-slate-400';
      case 3:
        return 'bg-gradient-to-br from-orange-400 to-orange-600';
      default:
        return 'bg-slate-200';
    }
  };

  const getMedalIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-600" />;
      case 2:
        return <Medal className="w-5 h-5 text-slate-600" />;
      case 3:
        return <Medal className="w-5 h-5 text-orange-600" />;
      default:
        return null;
    }
  };

  const currentUserRank = leaderboardData.findIndex(
    (entry) => entry.userId === currentUser?.uid
  ) + 1;

  const percentile = currentUserRank
    ? Math.round((1 - currentUserRank / leaderboardData.length) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 relative overflow-hidden">
      {/* Animated Background Circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Header */}
      <div className="relative z-10 px-4 py-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/main')}
            className="group flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-xl transition-all"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-semibold">Back</span>
          </button>

          <h1 className="text-3xl md:text-4xl font-black text-white text-center flex items-center gap-3">
            <Trophy className="w-10 h-10" />
            Leaderboard
          </h1>

          <button
            onClick={() => navigate('/main')}
            className="p-2 hover:bg-white/10 backdrop-blur-md rounded-xl transition-all"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pb-10">
        {/* Tabs */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setActiveTab('weekly')}
            className={`flex-1 px-6 py-4 rounded-2xl font-bold text-lg transition-all ${
              activeTab === 'weekly'
                ? 'bg-white text-indigo-600 shadow-2xl scale-105'
                : 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20'
            }`}
          >
            🔥 Weekly
          </button>
          <button
            onClick={() => setActiveTab('alltime')}
            className={`flex-1 px-6 py-4 rounded-2xl font-bold text-lg transition-all ${
              activeTab === 'alltime'
                ? 'bg-white text-indigo-600 shadow-2xl scale-105'
                : 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20'
            }`}
          >
            ⭐ All Time
          </button>
        </div>

        {/* Percentile Banner */}
        {currentUserRank > 0 && activeTab === 'weekly' && (
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 rounded-2xl p-6 mb-6 shadow-2xl animate-in slide-in-from-top duration-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/30 backdrop-blur-sm rounded-2xl w-16 h-16 flex items-center justify-center">
                  <span className="text-3xl font-black text-white">#{currentUserRank}</span>
                </div>
                <div>
                  <p className="text-white font-bold text-xl">
                    You're doing better than
                  </p>
                  <p className="text-white font-black text-2xl">
                    {percentile}% of other players!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Time Remaining */}
        {activeTab === 'weekly' && (
          <div className="flex justify-end mb-4">
            <div className="bg-white/10 backdrop-blur-md rounded-full px-5 py-3 flex items-center gap-3">
              <Clock className="w-5 h-5 text-white" />
              <span className="text-white text-sm font-bold">{timeRemaining}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="loader"></div>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {leaderboardData.length >= 3 && (
              <div className="flex items-end justify-center gap-6 mb-10 px-8">
                {/* 2nd Place */}
                <div className="flex flex-col items-center animate-in slide-in-from-bottom duration-700 delay-200">
                  <div className="relative mb-4">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-200 to-pink-300 p-1.5 shadow-xl">
                      <img
                        src={leaderboardData[1]?.photoURL || Avatar}
                        alt="2nd place"
                        className="w-full h-full rounded-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-slate-300 rounded-full flex items-center justify-center shadow-xl border-4 border-white">
                      <span className="text-sm font-black text-slate-700">2</span>
                    </div>
                  </div>
                  <p className="text-white font-bold text-base mb-2 text-center max-w-[100px] truncate">
                    {leaderboardData[1]?.displayName || 'Player 2'}
                  </p>
                  <div className="bg-white/20 backdrop-blur-md rounded-xl px-5 py-3 mb-4">
                    <p className="text-white text-sm font-bold whitespace-nowrap">
                      {leaderboardData[1]?.points || leaderboardData[1]?.score || 0} QP
                    </p>
                  </div>
                  <div className="w-28 h-36 bg-white/10 backdrop-blur-md rounded-t-3xl flex items-center justify-center shadow-2xl">
                    <span className="text-6xl font-black text-white/60">2</span>
                  </div>
                </div>

                {/* 1st Place */}
                <div className="flex flex-col items-center -mt-12 animate-in slide-in-from-bottom duration-700">
                  <div className="relative mb-4">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-400 p-2 shadow-2xl">
                      <img
                        src={leaderboardData[0]?.photoURL || Avatar}
                        alt="1st place"
                        className="w-full h-full rounded-full object-cover"
                      />
                    </div>
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <Crown className="w-10 h-10 text-yellow-300 drop-shadow-2xl animate-bounce" />
                    </div>
                    <div className="absolute -bottom-3 -right-3 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                      <span className="text-lg font-black text-yellow-800">1</span>
                    </div>
                  </div>
                  <p className="text-white font-bold text-lg mb-2 text-center">
                    {leaderboardData[0]?.displayName || 'Player 1'}
                  </p>
                  <div className="bg-white/20 backdrop-blur-md rounded-xl px-6 py-3 mb-4">
                    <p className="text-white font-bold text-base">
                      {leaderboardData[0]?.points || leaderboardData[0]?.score || 0} QP
                    </p>
                  </div>
                  <div className="w-32 h-48 bg-gradient-to-b from-yellow-400/30 to-yellow-500/20 backdrop-blur-md rounded-t-3xl flex items-center justify-center shadow-2xl border-t-4 border-yellow-400">
                    <span className="text-7xl font-black text-white">1</span>
                  </div>
                </div>

                {/* 3rd Place */}
                <div className="flex flex-col items-center animate-in slide-in-from-bottom duration-700 delay-400">
                  <div className="relative mb-4">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-200 to-blue-300 p-1.5 shadow-xl">
                      <img
                        src={leaderboardData[2]?.photoURL || Avatar}
                        alt="3rd place"
                        className="w-full h-full rounded-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-orange-400 rounded-full flex items-center justify-center shadow-xl border-4 border-white">
                      <span className="text-sm font-black text-orange-800">3</span>
                    </div>
                  </div>
                  <p className="text-white font-bold text-base mb-2 text-center max-w-[100px] truncate">
                    {leaderboardData[2]?.displayName || 'Player 3'}
                  </p>
                  <div className="bg-white/20 backdrop-blur-md rounded-xl px-5 py-3 mb-4">
                    <p className="text-white text-sm font-bold whitespace-nowrap">
                      {leaderboardData[2]?.points || leaderboardData[2]?.score || 0} QP
                    </p>
                  </div>
                  <div className="w-28 h-32 bg-white/10 backdrop-blur-md rounded-t-3xl flex items-center justify-center shadow-2xl">
                    <span className="text-6xl font-black text-white/60">3</span>
                  </div>
                </div>
              </div>
            )}

            {/* Full List */}
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden">
              <div className="divide-y divide-slate-100">
                {leaderboardData.map((entry, index) => {
                  const rank = index + 1;
                  const isCurrentUser = entry.userId === currentUser?.uid;

                  return (
                    <div
                      key={entry.id || index}
                      className={`flex items-center gap-4 p-5 transition-all ${
                        isCurrentUser
                          ? 'bg-indigo-50 hover:bg-indigo-100'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      {/* Rank */}
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                        {rank <= 3 ? (
                          <div
                            className={`w-12 h-12 rounded-full ${getMedalColor(
                              rank
                            )} flex items-center justify-center shadow-lg`}
                          >
                            {getMedalIcon(rank)}
                          </div>
                        ) : (
                          <span className="text-slate-400 font-black text-xl">{rank}</span>
                        )}
                      </div>

                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div
                          className={`w-14 h-14 rounded-full ${
                            rank === 1
                              ? 'bg-gradient-to-br from-yellow-200 to-yellow-300'
                              : rank === 2
                              ? 'bg-gradient-to-br from-slate-200 to-slate-300'
                              : rank === 3
                              ? 'bg-gradient-to-br from-orange-200 to-orange-300'
                              : 'bg-gradient-to-br from-indigo-200 to-indigo-300'
                          } p-1`}
                        >
                          <img
                            src={entry.photoURL || Avatar}
                            alt={entry.displayName || `Player ${rank}`}
                            className="w-full h-full rounded-full object-cover"
                          />
                        </div>
                        {entry.country && (
                          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white flex items-center justify-center text-sm shadow-lg">
                            {entry.country === 'IN' && '🇮🇳'}
                            {entry.country === 'US' && '🇺🇸'}
                            {entry.country === 'CA' && '🇨🇦'}
                            {entry.country === 'FR' && '🇫🇷'}
                            {!['IN', 'US', 'CA', 'FR'].includes(entry.country) && '🌍'}
                          </div>
                        )}
                      </div>

                      {/* Name and Points */}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-bold text-lg truncate ${
                            isCurrentUser ? 'text-indigo-700' : 'text-slate-900'
                          }`}
                        >
                          {entry.displayName || entry.username || `Player ${rank}`}
                        </p>
                        <p className="text-slate-500 font-semibold">
                          {entry.points || entry.score || 0} points
                        </p>
                      </div>

                      {/* Medal Badge */}
                      {rank <= 3 && (
                        <div className={`w-14 h-14 rounded-2xl ${getMedalColor(rank)} flex items-center justify-center shadow-lg flex-shrink-0`}>
                          {getMedalIcon(rank)}
                        </div>
                      )}
                    </div>
                  );
                })}

                {leaderboardData.length === 0 && (
                  <div className="p-16 text-center">
                    <Trophy className="w-20 h-20 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-400 font-bold text-lg">No data yet</p>
                    <p className="text-slate-300">
                      Be the first to complete a quiz!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LeaderboardFullscreen;
